import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Table, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Switch, 
  CircularProgress, 
  TextField, 
  Button, 
  Fab, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';


const AdminOrganizations = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    email: '',
    phone_number: ''
  });

  const navigate = useNavigate();
  
  const fetchAdminDashboard = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    setLoading(false);

    try {
      const response = await axios.get(`${API_BASE_URL}/admin/orgs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOrgs(response.data);
      setLoading(false);

    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      }
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchAdminDashboard();
  }, []);

  const handleToggle = async (orgId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/orgs/${orgId}`, {
        is_enabled: !orgs.find(org => org.org_id === orgId).is_enabled,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 200) {
        // pop up error message
        alert('Error updating admin status');
        return;
      }

      window.location.reload();

      const updatedOrgs = [...orgs];
      updatedOrgs.forEach(org => {
        if (org.org_id === orgId) {
          org.is_enabled = !org.is_enabled;
        }
      });
      setOrgs(updatedOrgs);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/');
      }
      console.error('Error updating admin status:', error);
    }
  };

  const handleFieldChange = (orgId, field, value) => {
    setOrgs(prevOrgs => 
      prevOrgs.map(org => 
        org.org_id === orgId 
          ? { ...org, [field]: field === 'is_enabled' ? Number(value) : value } 
          : org
      )
    );
  };

  const handleSave = async (orgId) => {
    const org = orgs.find(org => org.org_id === orgId);

    if (!org.org_name || !org.org_email || !org.org_number) {
      // pop up error message
      alert('Please fill in all fields');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const payload = {
        org_id: org.org_id,
        org_name: org.org_name,
        org_email: org.org_email,
        org_number: org.org_number
      };

      const response = await axios.post(`${API_BASE_URL}/admin/org-params`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });

      if (response.status !== 200) {
        // pop up error message
        alert('Error updating org params');
        return;
      }

      window.location.reload();
    } catch (error) {
      console.error('Error updating org:', error);
      throw error;
    }
  }

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewOrg({ name: '', email: '', phone_number: '' });
  };

  const handleNewOrgChange = (e) => {
    const { name, value } = e.target;
    setNewOrg(prev => ({ ...prev, [name]: value }));
  };

  const handleAddOrg = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/new-org`, newOrg, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 201) {
        window.location.reload();
      } else if (response.status === 406) {
        // pop up error message
        alert('Admin not exists, please create admin before create org');
      } else {
        // pop up error message
        alert('Error adding new org');
      }
    } catch (error) {
      console.error('Error adding new org:', error);
    }
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{ maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Enabled</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => (
                <TableRow key={org.org_id}>
                  <TableCell>
                    {editing === org.org_id ? (
                      <TextField 
                        size="small"
                        value={org.org_name} 
                        onChange={(e) => handleFieldChange(org.org_id, 'org_name', e.target.value)}
                      />
                    ) : org.org_name}
                  </TableCell>
                  <TableCell>
                    {org.logo_base64 && (
                      <img 
                        src={org.logo_base64}
                        alt="Organization Logo"
                        style={{ 
                          maxWidth: '100px', 
                          maxHeight: '50px',
                          objectFit: 'contain'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {editing === org.org_id ? (
                      <TextField 
                        size="small"
                        value={org.org_email} 
                        onChange={(e) => handleFieldChange(org.org_id, 'org_email', e.target.value)}
                      />
                    ) : org.org_email}
                  </TableCell>
                  <TableCell>
                    {editing === org.org_id ? (
                      <TextField 
                        size="small"
                        value={org.org_number} 
                        onChange={(e) => handleFieldChange(org.org_id, 'org_number', e.target.value)}
                      />
                    ) : org.org_number}
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={org.is_enabled} 
                      onChange={() => handleToggle(org.org_id)}
                    />
                  </TableCell>
                  <TableCell>
                    {editing === org.org_id ? (
                      <>
                        <Button 
                          variant="contained" 
                          color="success" 
                          size="small"
                          onClick={() => handleSave(org.org_id)}
                          sx={{ mr: 1 }}
                        >
                          Save
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          size="small"
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Box display="flex" gap={1}>
                        <Button 
                          variant="contained" 
                          size="small"
                          color="primary"
                          onClick={() => setEditing(org.org_id)}
                          sx={{ 
                            textTransform: 'none',
                            minWidth: '80px'
                          }}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outlined" 
                          size="small"
                          color="secondary"
                          onClick={() => navigate(`/admin/organizations/${org.org_id}`)}
                          sx={{ 
                            textTransform: 'none',
                            minWidth: '80px'
                          }}
                        >
                          Details
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Fab 
        color="primary" 
        aria-label="add" 
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleOpenDialog}
      >
        <AddIcon />
      </Fab>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{color: 'black'}}>Add New Organization</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Organization Name"
            type="text"
            fullWidth
            variant="standard"
            value={newOrg.name}
            onChange={handleNewOrgChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="standard"
            value={newOrg.email}
            onChange={handleNewOrgChange}
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="phone_number"
            label="Phone Number"
            type="text"
            fullWidth
            variant="standard"
            value={newOrg.phone_number}
            onChange={handleNewOrgChange}
            multiline
            rows={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleAddOrg}>Add Org</Button>
        </DialogActions>
      </Dialog>
          </Box>
        );
      };

export default AdminOrganizations;
